// Фоновый эмбиент, пока агент думает. Шесть характеров на выбор (см. AmbientId):
// от «дышащего дрона» до «пульса процесса». Все — тихие, зацикленные, с плавным
// появлением/затуханием. Звук генерируется в реальном времени через Web Audio,
// без файлов. Вариант по умолчанию — 'pulse'.

import type { AmbientId } from '../net/settings'

type Built = { sources: Array<{ stop: (t?: number) => void }>; timers: number[] }

export class ThinkingSound {
  private ctx: AudioContext | null = null
  private active: { master: GainNode; built: Built } | null = null
  private volume: number
  private variant: AmbientId
  private noiseBuf: AudioBuffer | null = null

  constructor(variant: AmbientId = 'pulse', volume = 0.9) {
    this.variant = variant
    this.volume = volume
  }

  /** Сменить характер звука. Применится при следующем start(). */
  setVariant(v: AmbientId): void {
    this.variant = v
  }

  private ensureCtx(): AudioContext {
    if (!this.ctx) {
      const Ctx: typeof AudioContext =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      this.ctx = new Ctx()
    }
    return this.ctx
  }

  /** Разблокировать звук в пользовательском жесте (iOS). */
  async unlock(): Promise<void> {
    const ctx = this.ensureCtx()
    if (ctx.state === 'suspended') await ctx.resume()
  }

  // Мягкий «бурый» шум (низкочастотный, не шипящий) — для воздуха/ленты/воды.
  private getNoise(ctx: AudioContext): AudioBuffer {
    if (this.noiseBuf) return this.noiseBuf
    const len = Math.floor(ctx.sampleRate * 3)
    const buf = ctx.createBuffer(1, len, ctx.sampleRate)
    const data = buf.getChannelData(0)
    let last = 0
    for (let i = 0; i < len; i++) {
      const w = Math.random() * 2 - 1
      last = (last + 0.02 * w) / 1.02
      data[i] = last * 3.2
    }
    this.noiseBuf = buf
    return buf
  }

  private osc(ctx: AudioContext, type: OscillatorType, freq: number): OscillatorNode {
    const o = ctx.createOscillator()
    o.type = type
    o.frequency.value = freq
    return o
  }

  // Медленный LFO, модулирующий любой AudioParam (громкость/частоту).
  private lfo(ctx: AudioContext, rate: number, depth: number, target: AudioParam, base?: number): OscillatorNode {
    const o = this.osc(ctx, 'sine', rate)
    const g = ctx.createGain()
    g.gain.value = depth
    o.connect(g)
    g.connect(target)
    if (base !== undefined) target.value = base
    return o
  }

  private build(ctx: AudioContext, out: GainNode): Built {
    switch (this.variant) {
      case 'drone':
        return this.buildDrone(ctx, out)
      case 'sonar':
        return this.buildSonar(ctx, out)
      case 'shimmer':
        return this.buildShimmer(ctx, out)
      case 'tape':
        return this.buildTape(ctx, out)
      case 'water':
        return this.buildWater(ctx, out)
      case 'pulse':
      default:
        return this.buildPulse(ctx, out)
    }
  }

  // 1. Дышащий дрон — тёплый пэд, медленно «дышит».
  private buildDrone(ctx: AudioContext, out: GainNode): Built {
    const sources: OscillatorNode[] = []
    const lp = ctx.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.value = 650
    lp.connect(out)
    ;[55, 110, 164.8].forEach((f, i) => {
      const o = this.osc(ctx, i === 0 ? 'sine' : 'triangle', f)
      const g = ctx.createGain()
      g.gain.value = i === 0 ? 0.16 : 0.06 / i
      o.connect(g)
      g.connect(lp)
      const l = this.lfo(ctx, 0.13 + i * 0.03, g.gain.value * 0.7, g.gain, g.gain.value)
      sources.push(o, l)
    })
    sources.forEach((s) => s.start())
    return { sources, timers: [] }
  }

  // 2. Сонар — низкий гул + редкий мягкий пинг.
  private buildSonar(ctx: AudioContext, out: GainNode): Built {
    const drone = this.osc(ctx, 'sine', 48)
    const dg = ctx.createGain()
    dg.gain.value = 0.16
    drone.connect(dg)
    dg.connect(out)
    const dl = this.lfo(ctx, 0.08, 0.05, dg.gain, 0.16)

    const tick = this.osc(ctx, 'sine', 620)
    const tg = ctx.createGain()
    tg.gain.value = 0
    const bp = ctx.createBiquadFilter()
    bp.type = 'bandpass'
    bp.frequency.value = 620
    bp.Q.value = 6
    tick.connect(tg)
    tg.connect(bp)
    bp.connect(out)

    const sources = [drone, dl, tick]
    sources.forEach((s) => s.start())

    const timers: number[] = []
    const ping = () => {
      const t = ctx.currentTime
      tg.gain.cancelScheduledValues(t)
      tg.gain.setValueAtTime(0.0001, t)
      tg.gain.linearRampToValueAtTime(0.12, t + 0.04)
      tg.gain.exponentialRampToValueAtTime(0.0001, t + 1.4)
      timers.push(window.setTimeout(ping, 3600))
    }
    timers.push(window.setTimeout(ping, 1200))
    return { sources, timers }
  }

  // 3. Мерцание — переливы поверх тёплого фона.
  private buildShimmer(ctx: AudioContext, out: GainNode): Built {
    const base = this.osc(ctx, 'sine', 98)
    const bg = ctx.createGain()
    bg.gain.value = 0.12
    base.connect(bg)
    bg.connect(out)
    const sources: OscillatorNode[] = [base]
    ;[784, 1046, 1318, 1568].forEach((f, i) => {
      const s = this.osc(ctx, 'sine', f)
      const sg = ctx.createGain()
      sg.gain.value = 0
      s.connect(sg)
      sg.connect(out)
      const l = this.lfo(ctx, 0.07 + i * 0.05, 0.02, sg.gain, 0.022)
      sources.push(s, l)
    })
    sources.forEach((s) => s.start())
    return { sources, timers: [] }
  }

  // 4. Ламповое тепло — аналоговый «воздух» + тёплый тон.
  private buildTape(ctx: AudioContext, out: GainNode): Built {
    const o = this.osc(ctx, 'triangle', 87)
    const g = ctx.createGain()
    g.gain.value = 0.12
    const lp = ctx.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.value = 900
    o.connect(g)
    g.connect(lp)
    lp.connect(out)
    const l = this.lfo(ctx, 0.1, 0.04, g.gain, 0.12)

    const n = ctx.createBufferSource()
    n.buffer = this.getNoise(ctx)
    n.loop = true
    const hp = ctx.createBiquadFilter()
    hp.type = 'highpass'
    hp.frequency.value = 300
    const nlp = ctx.createBiquadFilter()
    nlp.type = 'lowpass'
    nlp.frequency.value = 2200
    const ng = ctx.createGain()
    ng.gain.value = 0.02
    n.connect(hp)
    hp.connect(nlp)
    nlp.connect(ng)
    ng.connect(out)

    const sources = [o, l, n]
    sources.forEach((s) => s.start())
    return { sources, timers: [] }
  }

  // 5. Природный минимум — размытые вода и ветер.
  private buildWater(ctx: AudioContext, out: GainNode): Built {
    const n = ctx.createBufferSource()
    n.buffer = this.getNoise(ctx)
    n.loop = true
    const lp = ctx.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.value = 480
    lp.Q.value = 0.6
    const g = ctx.createGain()
    g.gain.value = 0.22
    n.connect(lp)
    lp.connect(g)
    g.connect(out)
    const lf = this.lfo(ctx, 0.06, 170, lp.frequency, 480)
    const lg = this.lfo(ctx, 0.09, 0.06, g.gain, 0.22)

    const sub = this.osc(ctx, 'sine', 60)
    const sg = ctx.createGain()
    sg.gain.value = 0.07
    sub.connect(sg)
    sg.connect(out)

    const sources = [n, lf, lg, sub]
    sources.forEach((s) => s.start())
    return { sources, timers: [] }
  }

  // 6. Пульс процесса (по умолчанию) — тёплый дрон + еле слышный медленный пульс.
  private buildPulse(ctx: AudioContext, out: GainNode): Built {
    const o = this.osc(ctx, 'sine', 58)
    const g = ctx.createGain()
    g.gain.value = 0.15
    const lp = ctx.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.value = 500
    o.connect(g)
    g.connect(lp)
    lp.connect(out)
    const dl = this.lfo(ctx, 0.1, 0.04, g.gain, 0.15)

    const p = this.osc(ctx, 'sine', 110)
    const pg = ctx.createGain()
    pg.gain.value = 0
    const plp = ctx.createBiquadFilter()
    plp.type = 'lowpass'
    plp.frequency.value = 300
    p.connect(pg)
    pg.connect(plp)
    plp.connect(out)

    const sources = [o, dl, p]
    sources.forEach((s) => s.start())

    const timers: number[] = []
    const beat = () => {
      const t = ctx.currentTime
      pg.gain.cancelScheduledValues(t)
      pg.gain.setValueAtTime(0.0001, t)
      pg.gain.linearRampToValueAtTime(0.1, t + 0.05)
      pg.gain.exponentialRampToValueAtTime(0.0001, t + 0.5)
      timers.push(window.setTimeout(beat, 1000))
    }
    timers.push(window.setTimeout(beat, 400))
    return { sources, timers }
  }

  start(): void {
    const ctx = this.ensureCtx()
    if (ctx.state === 'suspended') ctx.resume()
    if (this.active) return

    const now = ctx.currentTime
    const master = ctx.createGain()
    master.gain.setValueAtTime(0.0001, now)
    master.gain.linearRampToValueAtTime(this.volume, now + 0.8)
    master.connect(ctx.destination)

    const built = this.build(ctx, master)
    this.active = { master, built }
  }

  stop(): void {
    if (!this.active) return
    const ctx = this.ctx!
    const { master, built } = this.active
    this.active = null
    built.timers.forEach((id) => clearTimeout(id))
    const t = ctx.currentTime
    master.gain.cancelScheduledValues(t)
    master.gain.setValueAtTime(Math.max(0.0001, master.gain.value), t)
    master.gain.linearRampToValueAtTime(0.0001, t + 0.5)
    const stopAt = t + 0.55
    built.sources.forEach((s) => {
      try {
        s.stop(stopAt)
      } catch {
        /* noop */
      }
    })
  }

  close(): void {
    this.stop()
    this.ctx?.close().catch(() => {})
    this.ctx = null
    this.noiseBuf = null
  }
}
